import React, { useState } from 'react';
import { Play, Volume2, VolumeX, Edit3, X, Check, Film } from 'lucide-react';
import { MentorAreaConfig } from '../../types/mentorship';
import { useAuth } from '../../contexts/AuthContext';

interface HeroVideoSpoilerProps {
  config: MentorAreaConfig;
  onUpdateYoutubeUrl?: (newUrl: string) => void;
  onWatchClick?: () => void;
}

export const HeroVideoSpoiler: React.FC<HeroVideoSpoilerProps> = ({
  config,
  onUpdateYoutubeUrl,
  onWatchClick,
}) => {
  const { isMentor, isAdmin } = useAuth();
  // Autoplay automático sem som por padrão
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tempYoutubeUrl, setTempYoutubeUrl] = useState(config.heroSpoiler.youtubeUrl);

  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0] || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=1&rel=0` : url;
  };

  const handleSaveUrl = (e: React.FormEvent) => {
    e.preventDefault();
    if (onUpdateYoutubeUrl) {
      onUpdateYoutubeUrl(tempYoutubeUrl);
    }
    setIsEditModalOpen(false);
  };

  const embedUrl = getEmbedUrl(config.heroSpoiler.youtubeUrl);
  const accentColor = config.branding.accentColor || '#0FFF55';

  return (
    <div className="relative w-full overflow-hidden bg-[#121316] select-none -mt-4 md:-mt-8 -mx-4 md:-mx-10 w-[calc(100%+2rem)] md:w-[calc(100%+5rem)]">
      {/* Full-width Hero Container */}
      <div className="relative w-full h-[65vh] min-h-[480px] max-h-[700px] bg-black overflow-hidden">
        {isPlaying && embedUrl ? (
          <div className="relative w-full h-full">
            <iframe
              src={embedUrl}
              title={config.heroSpoiler.title}
              className="w-full h-full border-0 pointer-events-auto"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {/* Controles Flutuantes no Canto Inferior */}
            <div className="absolute bottom-6 left-6 md:left-14 right-6 flex items-center justify-between pointer-events-auto z-20">
              <div className="flex items-center gap-3">
                {onWatchClick && (
                  <button
                    onClick={onWatchClick}
                    className="flex items-center gap-2.5 px-6 py-3 rounded-xl bg-white hover:bg-gray-100 text-black font-extrabold text-xs shadow-2xl hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  >
                    <Play className="w-4 h-4 fill-black text-black" />
                    <span>Ir para o Curso</span>
                  </button>
                )}

                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/20 text-white text-xs font-bold transition-all shadow-xl"
                  title={isMuted ? 'Ativar som' : 'Desativar som'}
                >
                  {isMuted ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4 text-[#0FFF55]" />}
                  <span>{isMuted ? 'Clique para ativar som' : 'Som Ativado'}</span>
                </button>
              </div>

              {(isMentor || isAdmin) && (
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/20 text-gray-300 hover:text-white text-xs font-semibold transition-all shadow-xl"
                >
                  <Edit3 className="w-3.5 h-3.5" style={{ color: accentColor }} />
                  <span>Editar Vídeo</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Background Cover Image */}
            <img
              src={config.branding.coverImageUrl}
              alt={config.heroSpoiler.title}
              className="w-full h-full object-cover object-top opacity-90 scale-105"
            />

            {/* Hubla-style Seamless Gradient Blends */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#121316] via-[#121316]/75 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#121316]/80 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121316]/60 via-transparent to-transparent" />

            {/* Hero Content Overlay */}
            <div className="absolute inset-0 px-6 md:px-14 pb-12 flex flex-col justify-end gap-3 z-10">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-tight max-w-4xl drop-shadow-xl">
                {config.heroSpoiler.title}
              </h1>

              {/* Progress Line and Duration Indicator */}
              <div className="flex items-center gap-4 my-2 max-w-md">
                <div className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="w-0 h-full rounded-full transition-all duration-300"
                    style={{ backgroundColor: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
                  />
                </div>
                <span className="text-xs font-semibold text-gray-300">
                  {config.heroSpoiler.durationText}
                </span>
              </div>

              {/* Action Buttons Row */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (onWatchClick) {
                        onWatchClick();
                      } else {
                        setIsPlaying(true);
                      }
                    }}
                    className="flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-white hover:bg-gray-100 text-black font-extrabold text-sm shadow-2xl hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  >
                    <Play className="w-4 h-4 fill-black text-black" />
                    <span>Assista</span>
                  </button>

                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-3.5 rounded-xl bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 text-white transition-all"
                    title={isMuted ? 'Ativar som' : 'Desativar som'}
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>

                {(isMentor || isAdmin) && (
                  <button
                    onClick={() => setIsEditModalOpen(true)}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/20 text-gray-300 hover:text-white text-xs font-semibold transition-all"
                  >
                    <Edit3 className="w-3.5 h-3.5" style={{ color: accentColor }} />
                    <span>Editar Vídeo (YouTube)</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal para Editar Link do YouTube */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl bg-[#17181c] border border-white/10 p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Film className="w-4 h-4" style={{ color: accentColor }} />
                <span>Editar Spoiler de Vídeo</span>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveUrl} className="mt-4 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  URL do Vídeo do YouTube (Trailer / 1ª Aula)
                </label>
                <input
                  type="url"
                  required
                  value={tempYoutubeUrl}
                  onChange={(e) => setTempYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white placeholder-gray-500 focus:outline-none"
                  style={{ borderColor: accentColor }}
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs shadow-lg active:scale-95 transition-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Salvar Alterações</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
